/**
 * Product view data source module.
 *
 * This file keeps API and cache behavior out of the shared UI hook. Add new
 * load/save endpoints here when a product view needs different persistence
and keep the * visual state in `useProductViewCommon`.
 */
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { changeRequestApiClient } from '@/clients/ChangeRequestAPIClient';
import { productApiClient } from '@/clients/ProductAPIClient';
import { IAlbum } from '@/data/models/album';
import { IChangeRequest } from '@/data/models/changeRequest';
import {
  ReloadTracksContext,
  UseProductViewCommonProps,
} from '@/hooks/useProductViewCommon';
import { buildCurrentChanges } from '@/utils/changeRequest/buildCurrentChanges';
import { ProductViewModes } from '@/utils/productStateUtils';

type QueryId = string | number | null | undefined;

/** React Query has used both numeric and string IDs, so invalidate both shapes. */
const getQueryIdVariants = (id: QueryId) => {
  if (id == null) return [];

  return Array.from(new Set([id, id.toString()]));
};

/**
 * Groups cache invalidations needed after pricing/restriction updates.
 *
 * Add new invalidation helpers here when a save operation changes cached data
 * that is shared by multiple product view modes.
 */
const useProductViewInvalidations = () => {
  const queryClient = useQueryClient();

  const invalidateAllowedSalesTeams = async (
    albumId?: string | number | null
  ) => {
    await Promise.all(
      getQueryIdVariants(albumId).map((id) =>
        queryClient.invalidateQueries({
          queryKey: ['allowedSalesTeams', id],
        })
      )
    );
  };

  const invalidateAllowedCRSalesTeams = async (
    albumId?: string | number | null,
    changeRequestId?: string | number | null
  ) => {
    await Promise.all(
      getQueryIdVariants(albumId).flatMap((id) =>
        getQueryIdVariants(changeRequestId).map((crId) =>
          queryClient.invalidateQueries({
            queryKey: ['allowedCRSalesTeams', id, crId],
          })
        )
      )
    );
  };

  return {
    invalidateAllowedSalesTeams,
    invalidateAllowedCRSalesTeams,
  };
};

/**
 * Creates the endpoint adapter consumed by `useProductViewCommon`.
 *
 * Product modes call the product API directly. Change Request mode loads the CR
 * wrapper, uses its `changeRequestAlbum` as the editable album, and saves the
 * updated album back inside the CR payload.
 */
export const useProductViewDataSource = (
  productViewMode: ProductViewModes
): UseProductViewCommonProps => {
  const { invalidateAllowedSalesTeams, invalidateAllowedCRSalesTeams } =
    useProductViewInvalidations();
  const [changeRequest, setChangeRequest] = useState<IChangeRequest>();
  const isChangeRequest =
    productViewMode === ProductViewModes.ChangeRequest ||
    productViewMode === ProductViewModes.ChangeRequestReadOnly;
  const currentChanges = useMemo(() => {
    if (!isChangeRequest) {
      return undefined;
    }

    return buildCurrentChanges(changeRequest);
  }, [changeRequest, isChangeRequest]);

  /**
   * Reads the active Change Request and stores it so the view can render badges,
   * current changes, and submit updates with the latest payload shape.
   */
  const loadChangeRequest = async (albumId: string, changeRequestId = '') => {
    const response = changeRequestId
      ? await changeRequestApiClient.getChangeRequestById(
          albumId,
          changeRequestId
        )
      : await changeRequestApiClient.getActiveChangeRequest(albumId);

    const changeRequestData = response.data;

    if (!changeRequestData) {
      return undefined;
    }

    setChangeRequest(changeRequestData);
    return changeRequestData;
  };

  const loadAlbumData = async (albumId: string, changeRequestId?: string) => {
    if (!isChangeRequest) {
      const response = await productApiClient.getAlbum(albumId);
      return { album: response?.data, albumId };
    }

    const changeRequestData = await loadChangeRequest(albumId, changeRequestId);

    if (!changeRequestData) {
      return undefined;
    }

    return {
      album: changeRequestData.changeRequestAlbum,
      albumId: changeRequestData.albumId,
      validationChangeRequestId: changeRequestData.id,
    };
  };

  const reloadChangeRequestTracksData = async ({
    albumId,
  }: ReloadTracksContext) => {
    // Tracks in Change Request mode are nested on the CR album, so reload the CR.
    const changeRequestData = await loadChangeRequest(albumId);
    return changeRequestData?.changeRequestAlbum;
  };

  const saveAlbumData = async (updatedAlbum: IAlbum) => {
    if (!isChangeRequest) {
      const albumJson = JSON.stringify(updatedAlbum);
      await productApiClient.updateProduct(updatedAlbum.id!, albumJson);
      await invalidateAllowedSalesTeams(updatedAlbum.id);
      return { reloadAlbumId: updatedAlbum.id };
    }

    if (!changeRequest) return;

    await changeRequestApiClient.updateChangeRequest(
      changeRequest.albumId,
      changeRequest.id!,
      { ...changeRequest, changeRequestAlbum: updatedAlbum }
    );
    await invalidateAllowedCRSalesTeams(updatedAlbum.id, changeRequest.id);
    return { reloadAlbumId: updatedAlbum.id };
  };

  return {
    productViewMode,
    changeRequest: isChangeRequest ? changeRequest : undefined,
    currentChanges,
    loadAlbumData,
    reloadTracksData: isChangeRequest
      ? reloadChangeRequestTracksData
      : undefined,
    saveAlbumData,
  };
};
